import { WorkflowAwardExhibit } from "@codegen/enums";
import {
  FormContainer,
  OnboardingContainer,
  OnboardingFormField,
} from "@components/onboarding";
import { useReadOnlyMode } from "@components/readOnlyModeProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { OnboardingPageType } from "@pages/onboarding/types";
import { useDebounceCallback } from "@react-hook/debounce";
import { useLogError } from "@utils/error";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { z } from "zod";

const DescriptionFormSchema = z.object({
  description: z.string().min(1, "Required"),
});
type DescriptionFormValues = z.infer<typeof DescriptionFormSchema>;

export const DescriptionForm: React.FC<
  OnboardingPageType & {
    title: string;
    subtitle: React.ReactNode;
    description: React.ReactNode;
    footnote?: React.ReactNode;
    currentStep?: number;
    progress?: number;
  }
> = ({
  onSubmit,
  onBack,
  onSkip,
  autoSave,
  isSaving,
  exhibit,
  title,
  subtitle,
  description: descriptionProp,
  footnote,
  onSkipForNow,
  showSkipForNow,
  hideExitButton = false,
  currentStep,
  progress,
}) => {
  const { required, data } = exhibit;
  const { description }: { description?: string } = data ?? {};
  const [status, setStatus] = useState<
    "idle" | "loading" | "error" | "success"
  >("idle");
  const { isReadOnly } = useReadOnlyMode();
  const logError = useLogError();

  // Track pending save operations to avoid race conditions
  const pendingSaveRef = useRef<Promise<void> | null>(null);

  const methods = useForm({
    defaultValues: {
      description: description || "",
    },
    resolver: zodResolver(DescriptionFormSchema),
  });

  const { control, handleSubmit: rhfHandleSubmit } = methods;
  const watchedValues = useWatch({ control });

  const handleSave = useCallback(
    async (data: DescriptionFormValues) => {
      const savePromise = (async () => {
        try {
          await autoSave(data);
        } catch (e) {
          setStatus("error");
          logError(e, { message: "Error saving description" });
        } finally {
          pendingSaveRef.current = null;
        }
      })();

      pendingSaveRef.current = savePromise;
      await savePromise;
    },
    [autoSave, logError]
  );

  const processSubmit = useCallback(
    async (data: DescriptionFormValues) => {
      try {
        // Wait for any pending auto-save to complete
        if (pendingSaveRef.current) {
          await pendingSaveRef.current;
        }

        setStatus("loading");
        await onSubmit(data);
        setStatus("success");
      } catch (e) {
        setStatus("error");
        logError(e, { message: "Error submitting description" });
      } finally {
        if (status !== "success") setStatus("idle");
      }
    },
    [onSubmit, logError, status]
  );

  const handleSkipForNow = useCallback(async () => {
    try {
      if (watchedValues?.description) {
        await onSkipForNow?.({ description: watchedValues.description });
      } else {
        onSkipForNow?.();
      }
    } catch (e) {
      logError(e, { message: "Error skipping" });
    }
  }, [watchedValues, onSkipForNow, logError]);

  const debouncedSave = useDebounceCallback(handleSave, 300);

  useEffect(() => {
    if (watchedValues?.description) {
      debouncedSave({ description: watchedValues.description });
    }
  }, [watchedValues, debouncedSave]);

  return (
    <FormProvider {...methods}>
      <OnboardingContainer
        title={title}
        subtitle={subtitle}
        description={descriptionProp}
        footnote={footnote}
        onSubmit={rhfHandleSubmit(processSubmit)}
        exhibit={exhibit}
        name={`description-form`}
        onBack={onBack}
        onSkipForNow={required ? handleSkipForNow : onSkip}
        isLoading={status === "loading" || isSaving}
        currentStep={currentStep}
        progress={progress}
        showSkipForNow={showSkipForNow}
        hideExitButton={hideExitButton}
      >
        <FormContainer
          status={status}
          errorMessage="There was a problem saving your description. Please try again."
          successMessage={
            status === "success" ? "Description saved successfully." : undefined
          }
        >
          <OnboardingFormField
            name="description"
            type="textarea"
            required
            placeholder="Enter text here..."
            disabled={isReadOnly}
          />
        </FormContainer>
      </OnboardingContainer>
    </FormProvider>
  );
};

export const AwardDescriptionForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name, award } = exhibit as WorkflowAwardExhibit;

  const title = name ? name : `Awards - ${award.awardName}`;
  const subtitle = `Have you received recognition for your outstanding work? Whether it's an industry accolade, a professional honor, or a notable prize, we're excited to learn about it!`;
  return (
    <DescriptionForm
      {...props}
      title={module.name}
      subtitle={title}
      description={subtitle}
    />
  );
};

export const PressSummaryForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit as WorkflowAwardExhibit;

  const title = name;
  const subtitle = `Provide a summary for the press you received`;
  return (
    <DescriptionForm
      {...props}
      title={module.name}
      subtitle={title}
      description={subtitle}
    />
  );
};

export const OriginalContributionDescriptionForm: React.FC<
  OnboardingPageType
> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const title = name ? name : `Original Contributions of Major Significance`;
  const subtitle = (
    <div className="flex flex-col gap-3">
      <p>
        Have you created something innovative that's made waves in your
        industry? Maybe you've developed a groundbreaking technology, proposed a
        new theory, or pioneered a novel business approach? We're looking for
        evidence that your work has truly moved the needle.
      </p>
    </div>
  );

  return (
    <DescriptionForm
      {...props}
      title={module.name}
      subtitle={title}
      description={subtitle}
      footnote={
        <div className="font-light">
          <p className="text-sm">
            <b>For example:</b>
          </p>

          <p className="text-sm mt-2 mb-2">
            I designed an original{" "}
            <em>
              machine learning algorithm for predictive maintenance in
              industrial equipment
            </em>
            . My novel design is called <em>"CascadePredict"</em>. This is
            considered original because{" "}
            <em>
              it combines elements of reinforcement learning and anomaly
              detection to create a more adaptive and precise system for
              predicting equipment failures.
            </em>
          </p>
          <p className="text-sm">
            An expert in the field would describe my novel design as remarkable
            because{" "}
            <em>
              it significantly reduces false positives while improving early
              detection rates, saving companies like Tesla millions in
              unnecessary downtime and maintenance costs.
            </em>
          </p>
        </div>
      }
    />
  );
};

export default DescriptionForm;
